// apps/portals/admin/src/app.tsx — the shell the root route mounts (ADR
// ERP-FRONTEND-ARCHITECTURE §2: "app.tsx # shell: nav chrome, error/suspense
// roots"). Replaces router.tsx's former bare `() => <Outlet />`.
//
// THE ROOT STILL GATES NOTHING — router.tsx's documented choice is intact.
// This component only decides whether to draw CHROME: /login (and any future
// public screen listed in CHROMELESS) renders bare so an anonymous visitor
// never sees an empty sidebar frame; everything else gets the shell. Access
// itself is still decided per-screen by <ConsoleLayout>/screen gates, which is
// why an anonymous session reaching a gated route still lands on /login.
//
// ONE MORE THING THE SHELL DECIDES (ERP-DEPLOYMENT-MODEL §13, 2026-09-20):
// whether the route's MODULE is part of this installation. The route tree is
// the same on every install (routes are types; the module set is
// configuration), so a bookmarked /library page on a CMS-only install still
// matches — and the shell renders <ModuleUnavailableCard> in place of the
// Outlet: inside the chrome on a chromed route, so the sidebar stays and the
// person can go elsewhere; bare on a chromeless one (a phone camera page has
// no sidebar to keep — the card's Home link is the way out). The manifest
// comes from CapabilitiesProvider (main.tsx); the shell waits for it exactly
// as it waits for /me — for the SCREEN as well as the nav. The verdict is
// three-state (PORTALS-1, 2026-09-20): a module-owned route holds on the
// route skeleton while the manifest is in flight, because an Outlet mounted
// on the unrestricted default drew the library's All Titles screen on a
// CMS-only install — and fired its reads at a gateway that answers 404 — for
// as long as /api/capabilities took, then swapped in the card.
import { Match, Show, Suspense, Switch } from "solid-js";
import { Outlet, useNavigate, useRouterState } from "@tanstack/solid-router";
import { useCapabilities, useSession } from "@erp/auth-kit/fe";
import { resolveModuleOf } from "@erp/auth-kit/nav";
import { MODULE_LABELS } from "@erp/core-schemas";
import type { ModuleId } from "@erp/core-schemas";
import { signOutUser } from "@erp/module-auth/fe";
// Cross-module composition happens HERE, in the shell — the one place Law F1
// lets two modules meet. LIB's detail screens declare an audit-trail hole
// (@erp/ui/record-history); AUDIT owns the component that fills it.
// Neither module imports the other, so each stays its own chunk.
import { RecordHistory } from "@erp/module-audit/fe";
import { RecordHistoryProvider } from "@erp/ui/record-history";
// The third: every provenance line ("Created 7 days ago by …") names an opaque
// user id, and the directory that turns it into a name is AUTH's. LIB and FIN
// declare the hole (@erp/ui/actor-name); auth's resolver fills it here.
import { resolveActor, searchActors } from "@erp/module-auth/fe/directory";
import { ActorDirectoryProvider, ActorSearchProvider } from "@erp/ui/actor-name";
// The second shell-level composition: FIN's invoice panel can raise an invoice
// against an unpaid LIBRARY fine, but FIN must not import LIB. FIN names the
// shape (an outstanding charge); the shell backs it with lib's own endpoint and
// normalizes the domain words away at the boundary. `enabled` stays reactive so
// the fines page is fetched only while that panel is open in lib-fine mode.
import { useQuery, useQueryClient } from "@tanstack/solid-query";
import { libraryQueries } from "@erp/module-lib/fe";
// The pairing control rides the scan subpath, which is where that kit lives.
import { ScanWithPhoneButton } from "@erp/module-lib/fe/scan";
import { InvoiceableChargesProvider } from "@erp/module-fin/fe/invoiceable-charges";
import type { InvoiceableChargeSource } from "@erp/module-fin/fe/invoiceable-charges";
// The fourth: a CMS publication may point at its copy in the LIBRARY (db-cms 0024's
// library_ebook_id — an opaque id the CMS never joins), and picking one needs LIB's
// ebook search. The CMS names the hole (@erp/module-cms/fe/library-ebook-slot); the
// shell fills it with a LookupSelect over lib's ebook query — and ONLY when the install
// runs `lib` (the capabilities manifest, ERP-DEPLOYMENT-MODEL §13): on a CMS-only install
// the accessor answers null and the form renders no Library-copy section at all.
import { LibraryEbookPickerProvider } from "@erp/module-cms/fe/library-ebook-slot";
import type { LibraryEbookPicker, LibraryEbookPickerProps } from "@erp/module-cms/fe/library-ebook-slot";
import { LookupSelect } from "@erp/ui/lookup-select";

const libFinesAsCharges: InvoiceableChargeSource = (enabled) => {
  // Runs inside the CALLING screen's reactive scope — app.tsx only supplies the
  // function, it never holds the query itself.
  const q = useQuery(() => ({ ...libraryQueries.fines({ limit: 50, status: "Unpaid" }), enabled: enabled() }));
  return {
    charges: () =>
      (q.data?.data ?? []).map((f) => ({
        id: f.id,
        payerName: f.patronName,
        reason: f.reason,
        amountPaise: f.amountPaise,
        invoiceId: f.invoiceId,
      })),
    isLoading: () => q.isLoading,
  };
};

/** The page the picker searches — 20 titles per keystroke; the LookupSelect debounces. */
const LIBRARY_EBOOK_SEARCH_LIMIT = 20;

/**
 * The library-copy picker the shell hands the CMS: a searchable select over lib's ebook
 * catalogue (title · author), the chosen title read by id so the stored pick is labelled
 * even when the last search page did not contain it, and a "No library copy" row that
 * clears the link. Runs inside the CALLING form's reactive scope — app.tsx supplies the
 * component, the form mounts it.
 */
const LibraryEbookLookup: LibraryEbookPicker = (props: LibraryEbookPickerProps) => {
  const qc = useQueryClient();
  // The chosen ebook, by id, for its label — only while something is chosen.
  const chosen = useQuery(() => ({ ...libraryQueries.ebook(props.value), enabled: !!props.value }));
  const search = async (q: string) => {
    const page = await qc.fetchQuery(libraryQueries.ebooks({ limit: LIBRARY_EBOOK_SEARCH_LIMIT, search: q.trim() || undefined }));
    return page.data.map((e) => ({ value: e.id, label: e.author ? `${e.title} — ${e.author}` : e.title }));
  };
  return (
    <LookupSelect
      id={props.id}
      value={props.value}
      valueLabel={chosen.data ? (chosen.data.author ? `${chosen.data.title} — ${chosen.data.author}` : chosen.data.title) : undefined}
      search={search}
      onChange={(id, label) => props.onChange(id, label)}
      emptyLabel="No library copy"
      placeholder="Search the library's eBooks…"
      searchPlaceholder="Search by title or author"
      size="md"
      class="w-full"
    />
  );
};
import { AppShell } from "@erp/ui/app-shell";
import { ModuleUnavailableCard } from "@erp/ui/error-card";
import { PageSkeleton } from "@erp/ui/page-skeleton";
import { Toaster } from "@erp/ui/toast";
import { ADMIN_MANIFESTS, adminNav } from "./nav";

/** Screens that render without chrome — the door, and anything reachable before a session exists. */
// /accept-invite belongs here for the strongest reason on the list: the person
// opening it has no account at all, so a shell with an empty sidebar and a
// "Sign out" header would be furniture for a session that does not exist.
const CHROMELESS = ["/login", "/scanner", "/verify", "/setup", "/accept-invite", "/reset-password"];

/**
 * The chromeless routes a MODULE owns. No manifest row names them (a manifest
 * is the sidebar, and these have none), so `resolveModuleOf` cannot see them —
 * yet each mounts a module's screen: /setup is lib's first-run wizard, /verify
 * is lib's CardVerify (where a printed card's QR lands), /scanner is lib's
 * ScannerApp (the phone camera page). Prefix-matched like CHROMELESS. Only
 * /setup was mapped at first, and /verify and /scanner drew the library on a
 * CMS-only install (PORTALS-2 / V-04, 2026-09-20).
 */
const CHROMELESS_OWNER: Record<string, ModuleId> = { "/setup": "lib", "/verify": "lib", "/scanner": "lib" };

export function App() {
  const auth = useSession();
  const caps = useCapabilities();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // NEVER read auth.session() (solid-query's q.data) while the /me query is
  // pending: that read SUSPENDS, and the shell is the outermost component, so
  // there is no boundary above it — the whole page would go blank until /me
  // answers. screens/home.tsx has always guarded this way (`<Show when={!
  // auth.loading()}>` reads isPending first, q.data only inside); the shell
  // must too. Regression-tested in test/app-shell.test.tsx.
  const session = () => (auth.loading() ? null : auth.session());
  // The sidebar waits for BOTH round-trips: a nav built from the unrestricted
  // default while the manifest is in flight would flash Library at a CMS-only
  // install, then take it away. Empty until both have answered.
  const modules = () => (auth.loading() || caps.loading() ? [] : adminNav(session(), caps.modules()));
  const startsAt = (p: string) => pathname() === p || pathname().startsWith(`${p}/`);
  // The install module the current route belongs to — undefined on a route no
  // module owns (/, /profile, the door). The raw manifests are walked, not
  // the built nav: the built nav has already dropped the very module this
  // needs to name. The chromeless lib pages have no manifest row, so the
  // CHROMELESS_OWNER map names them.
  const owner = (): ModuleId | undefined => {
    const fromManifest = resolveModuleOf(pathname(), ADMIN_MANIFESTS);
    if (fromManifest) return fromManifest;
    const chromeless = Object.keys(CHROMELESS_OWNER).find(startsAt);
    return chromeless ? CHROMELESS_OWNER[chromeless] : undefined;
  };
  // The owner, when the install does NOT enable it and the manifest has
  // answered. Undefined while the manifest loads (no verdict yet) — `pending`
  // below is the state that covers that gap.
  const unavailable = (): ModuleId | undefined => {
    if (caps.loading()) return undefined;
    const module = owner();
    return module && !caps.has(module) ? module : undefined;
  };
  // A module-owned route with no verdict yet. An UNOWNED route (the door, the
  // profile, /) never waits: nothing behind it depends on the manifest.
  const pending = () => caps.loading() && Boolean(owner());
  const bare = () => CHROMELESS.some(startsAt);
  // The library-copy slot's value — an ACCESSOR the CMS form calls in its own reactive
  // scope (Solid's Provider reads `value` once, so a bare component could not follow the
  // manifest's arrival): null while the manifest loads and on an install without `lib`,
  // the picker once the manifest names the library.
  const libraryEbookPicker = () => (caps.has("lib") ? LibraryEbookLookup : null);

  // What the route slot shows, in both the chromed and the bare branch:
  // pending → the route skeleton (the same one a lazy chunk shows, so nothing
  // flickers between "chunk loading" and "manifest loading"); off → the card,
  // IN PLACE of the Outlet, never beside it (the screen behind a disabled
  // module would only fetch its way to the gateway's 404 — the route stays
  // registered, routes are types; this card is the mechanism); on, or unowned
  // → the Outlet. `heading`: the card IS the page, so it holds the h1. In the
  // bare branch the shell pads nothing (each chromeless screen frames itself,
  // ScannerApp's `mx-auto max-w-md p-6` being the model), so the skeleton and
  // the card take that same frame there — and only there, or a chromed page
  // would be padded twice.
  const RouteBody = (props: { bare?: boolean }) => {
    const frame = () => (props.bare ? "mx-auto max-w-md p-6" : undefined);
    return (
      <Switch
        fallback={
          <Suspense fallback={<PageSkeleton id="route-skeleton" />}>
            <Outlet />
          </Suspense>
        }
      >
        <Match when={pending()}>
          <div class={frame()}>
            <PageSkeleton id="route-skeleton" />
          </div>
        </Match>
        <Match when={unavailable()}>
          {(module) => (
            <div class={frame()}>
              <ModuleUnavailableCard id="module-unavailable" moduleLabel={MODULE_LABELS[module()]} heading />
            </div>
          )}
        </Match>
      </Switch>
    );
  };


  const signOut = async () => {
    try {
      await signOutUser();
    } finally {
      // Refetch through the provider (no hardcoded key): success → 401 → null.
      // A failed sign-out still clears local claims; the server session may live.
      await auth.refetch();
      await navigate({ to: "/login" });
    }
  };

  return (
    <RecordHistoryProvider value={RecordHistory}>
      <InvoiceableChargesProvider value={libFinesAsCharges}>
      <LibraryEbookPickerProvider value={libraryEbookPicker}>
      <ActorDirectoryProvider value={resolveActor}>
      <ActorSearchProvider value={searchActors}>
      <Toaster />
      <Show
        when={!bare()}
        fallback={
          /* A <div>, not a <main> (P-07, 2026-09-21): on a bare route the SCREEN owns the
             landmark — the login and register doors and ScannerApp each draw their own
             <main>, framed to their own width — and a shell <main> around them made two
             landmarks per page. A bare wire that mounts a module component drawing none
             (CardVerify, AcceptInviteScreen, PasswordResetScreen) adds the <main> itself. */
          <div class="min-h-screen">
            <RouteBody bare />
          </div>
        }
      >
        <AppShell
          modules={modules()}
          pathname={pathname()}
          user={session() ? { name: session()?.name ?? session()?.email ?? "Signed in", email: session()?.email } : null}
          onSignOut={signOut}
          onProfile={() => void navigate({ to: "/profile" })}
          appName="SHIPPRA ERP"
          /* PAIRING IS A HEADER CONTROL, not a page (2026-10-02, the owner).
             It was a rail item leading to /library/devices, which made a whole
             screen out of one dialog — the desk has opened the same dialog from
             a button since it was built. The portal is where this belongs: a
             module's component meeting the shell is exactly what `headerActions`
             is for, and @erp/ui cannot import from a module. */
          headerActions={<ScanWithPhoneButton compact contextHint="Scans land on whichever console screen you have open." />}
        >
          {/* Lazy route chunks resolve here, so the chrome stays put while one loads. */}
          <RouteBody />
        </AppShell>
      </Show>
      </ActorSearchProvider>
      </ActorDirectoryProvider>
      </LibraryEbookPickerProvider>
      </InvoiceableChargesProvider>
    </RecordHistoryProvider>
  );
}
